import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { noop } from 'rxjs';
import { Store } from '@ngxs/store';
import { TsSnackbarService } from 'tsui';
import { computeDistanceBetween, LatLng } from 'spherical-geometry-js';
import { AccountService } from 'app/core';
import { TableSortState } from '@shared/models';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { FieldDef, QueryConfig, SavedView, FieldDefType } from 'app/core/models';
import { PropertyNearbyService } from './nearby.service';
import { PropertiesService } from '../../properties.service';
import { Property } from 'app/modules/projects/projects.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { generateDownloadableFile, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';

@Component({
  selector: 'property-nearby',
  templateUrl: './nearby.component.html',
  styleUrls: ['./nearby.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyNearbyComponent {
  title: string = 'Nearby Properties';
  saved_view_name: string = 'Property Nearby';
  nearby_related_to_type: string = 'PropertyNearby';
  default_page_size = 20;
  default_page = 0;
  data: Data;
  property: Property;
  savedView: SavedView;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  queryFilters: Partial<QueryConfig.QueryFilters> = {};
  pageEvent: PageEvent;
  sortState: TableSortState;
  results_dataset: any;
  results_pagination: any;

  get fieldDefs(): FieldDef[] {
    let fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Property, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
    fieldDefs.push({
      name: 'dist_meters',
      label: 'Distance (miles)',
      type: 'number' as FieldDefType,
      related_to_type: RelatedToType.Property,
      meta: {
        scale: 2,
      },
    });
    return fieldDefs;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly propertiesService: PropertiesService,
    private readonly propertyNearbyService: PropertyNearbyService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly snackBar: TsSnackbarService,
    private readonly accountService: AccountService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data;
      this.property = data.property;

      // If savedView named 'Property Nearby' doesn't exist, create it.
      if (!this.data.savedViews.filter((view: SavedView) => view.name === this.saved_view_name)[0]) {
        const existingPropertyView: SavedView = this.data.savedViews.filter(
          (view: SavedView) => view.related_to_type === RelatedToType.Property,
        )[0];

        const nearbySavedView = {
          id: null,
          name: this.saved_view_name,
          related_to_type: this.nearby_related_to_type,
          definition: existingPropertyView.definition,
          account_id: existingPropertyView.account_id,
        };

        this.accountService
          .createSavedView(nearbySavedView)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response) => {
            this.savedView = response;
            this.setDisplayColumns();
          });
      } else {
        this.savedView = this.data.savedViews.filter((view: SavedView) => view.name === this.saved_view_name)[0];
        this.setDisplayColumns();
      }

      this.sortState = this.getSortState();

      if (
        this.propertyNearbyService.property.address &&
        this.propertyNearbyService.property.address.latitude &&
        this.propertyNearbyService.property.address.longitude
      ) {
        this.queryFilters.location = {
          near: {
            lat: parseFloat(this.propertyNearbyService.property.address.latitude?.toString()),
            lon: parseFloat(this.propertyNearbyService.property.address.longitude?.toString()),
          },
          within: '1000000mi',
        };
        this.queryFilters.q = '';

        this.pageEvent = new PageEvent();
        this.pageEvent.pageIndex = this.default_page;
        this.pageEvent.pageSize = this.default_page_size;

        this.getProperties();
      } else {
        this.results_dataset = [];
        this.results_pagination = { endIndex: 0, lastPage: 0, length: 0, page: 0, size: 20, startIndex: 0 };
      }
    });
  }

  private getSortState(): TableSortState {
    const savedSort = JSON.parse(this.savedView?.definition ?? '{}')?.table?.sort;

    return {
      active: savedSort?.active || 'title',
      direction: savedSort?.direction || 'asc',
    };
  }

  private setDisplayColumns(): void {
    this.displayColumns = removeArchivedFieldsFromTableDefinition(
      this.savedView ? JSON.parse(this.savedView.definition).table.fields : ['name', 'dist_meters'],
      this.fieldDefs,
    );

    // Filter out dist_meters column as GQL can't handle a dynamically created column.
    this.loopDisplayColumns = [...this.displayColumns].filter((item) => item !== 'dist_meters');
  }

  page(event: MatPaginator): void {
    this.pageEvent.pageSize = event.pageSize;
    this.pageEvent.pageIndex = event.pageIndex;
    this.getProperties();
  }

  sort(event: TableSortState): void {
    if (event.active === 'name') this.sortState.active = 'title';
    else this.sortState.active = event.active;
    this.sortState.direction = event.direction;
    this.getProperties();
  }

  search(event: Partial<QueryConfig.QueryFilters>): void {
    this.pageEvent.pageIndex = 0;
    this.cleanAndRequestProperties(event);
  }

  filter(event: Partial<QueryConfig.QueryFilters>): void {
    this.pageEvent.pageIndex = 0;
    this.cleanAndRequestProperties(event);
  }

  update(event: any): void {
    const options = { ...event.params, id: event.item?.id };
    const selectColumn = [];
    selectColumn.push(Object.keys(event.params)[0]);
    if (event.params[Object.keys(event.params)[0]] !== 'add_new') {
      this.propertiesService
        .saveProperty(options, event.item)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(noop);
    } else {
      this.getProperties();
    }
  }

  private cleanAndRequestProperties(event: Partial<QueryConfig.QueryFilters>): void {
    this.queryFilters = {
      ...this.queryFilters,
      fjson: event?.fjson || '[]',
      q: event?.q || '',
    };

    this.getProperties();
  }

  private getProperties(): void {
    this.propertiesService
      .getProperties(
        this.loopDisplayColumns.concat(['latitude', 'longitude']),
        this.queryFilters,
        this.pageEvent.pageIndex,
        this.pageEvent.pageSize,
        this.sortState.active,
        this.sortState.direction,
        this.queryFilters?.q,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        this.results_dataset = results.properties;

        for (const prop of this.results_dataset) {
          if (prop.latitude && prop.longitude) {
            const propertyLatLong = new LatLng(this.property.address.latitude, this.property.address.longitude);
            const compLatLong = new LatLng(prop.latitude, prop.longitude);
            prop.dist_meters = computeDistanceBetween(propertyLatLong, compLatLong) / 1609;
          } else prop.dist_meters = 0;
        }

        this.results_pagination = results.pagination;
        this.cdRef.markForCheck();
      });
  }

  exportCsv(): void {
    this.propertiesService
      .getPropertiesWithoutSort(
        this.loopDisplayColumns.concat(['latitude', 'longitude']),
        this.queryFilters,
        this.pageEvent.pageIndex,
        this.pageEvent.pageSize,
        this.queryFilters.q,
        FileFormat.CSV,
        '',
        false,
        this.savedView.id,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.message) {
          this.snackBar.open(data?.message);
        } else {
          generateDownloadableFile(data, FileFormat.CSV, `${this.property.name} Nearby Properties`);
        }
      });
  }
}
